import { merge } from 'lodash-es';
import { GlButton, GlDisclosureDropdown, GlDisclosureDropdownGroup, GlLoadingIcon, GlTooltipDirective, GlSearchBoxByType } from '../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { createAlert } from '../../alert.js?vue3';
import { s__, __, sprintf } from '~/locale';
import { reportToSentry } from '~/ci/utils';
import CiIcon from '../../vue_shared/components/ci_icon/ci_icon.vue?vue3';
import { fetchPolicies } from '../../lib/graphql.js?vue3';
import { FAILED_STATUS } from '~/ci/constants';
import JobDropdownItem from '../common/private/job_dropdown_item.vue?vue3';
import getPipelineStageJobsQuery from './graphql/queries/get_pipeline_stage_jobs.query.graphql';
import stageJobsUpdatedSubscription from './graphql/subscriptions/stage_jobs_updated.subscription.graphql';
const searchItemsThreshold = 12;
export default {
    name: 'PipelineStageDropdown',
    components: {
        CiIcon,
        GlButton,
        GlDisclosureDropdown,
        GlDisclosureDropdownGroup,
        GlLoadingIcon,
        GlSearchBoxByType,
        JobDropdownItem
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    props: {
        isMergeTrain: {
            type: Boolean,
            required: false,
            default: false
        },
        stage: {
            type: Object,
            required: true
        }
    },
    emits: [
        'job-action-executed',
        'mini-graph-stage-click'
    ],
    data () {
        return {
            isDropdownOpen: false,
            stageJobs: [],
            search: '',
            isSubscribed: false
        };
    },
    apollo: {
        stageJobs: {
            fetchPolicy: fetchPolicies.CACHE_AND_NETWORK,
            query: getPipelineStageJobsQuery,
            variables () {
                return {
                    id: this.stage.id
                };
            },
            skip () {
                return !this.isDropdownOpen;
            },
            update (data) {
                return data?.ciPipelineStage?.jobs?.nodes || [];
            },
            result ({ data }) {
                if (this.isDropdownOpen && !this.isSubscribed && data?.ciPipelineStage) {
                    this.subscribeToJobUpdates();
                }
            },
            error (error) {
                createAlert({
                    message: s__('Pipelines|There was a problem fetching the pipeline stage jobs.')
                });
                reportToSentry(this.$options.name, error);
            }
        }
    },
    computed: {
        dropdownHeaderText () {
            return sprintf(__('Stage: %{stageName}'), {
                stageName: this.stage.name
            });
        },
        dropdownTooltipTitle () {
            return this.isDropdownOpen ? '' : `${this.stage.name}: ${this.stage.detailedStatus.tooltip}`;
        },
        failedJobs () {
            return this.stageJobs.filter((job)=>job.detailedStatus.group === FAILED_STATUS);
        },
        failedJobsGroup () {
            // Jobs render through the default slot; `items` only satisfies the group prop validator.
            return {
                name: s__('Pipelines|Failed jobs'),
                items: this.failedJobs.map(({ name })=>({
                        text: name
                    }))
            };
        },
        hasFailedJobs () {
            return Boolean(this.failedJobs.length);
        },
        hasPassedJobs () {
            return Boolean(this.passedJobs.length);
        },
        isLoading () {
            return this.$apollo.queries.stageJobs.loading;
        },
        passedJobs () {
            return this.stageJobs.filter((job)=>job.detailedStatus.group !== FAILED_STATUS);
        },
        searchedJobs () {
            return this.stageJobs.filter((job)=>job.name.toLowerCase().includes(this.search.toLowerCase()));
        },
        searchVisible () {
            return !this.isLoading && this.stageJobs.length > searchItemsThreshold;
        }
    },
    watch: {
        isDropdownOpen (isOpen) {
            if (!isOpen) {
                this.unsubscribeFromJobUpdates();
            }
        }
    },
    beforeDestroy () {
        this.unsubscribeFromJobUpdates();
    },
    methods: {
        onShowDropdown () {
            this.isDropdownOpen = true;
            this.$emit('mini-graph-stage-click');
        },
        onHideDropdown () {
            this.isDropdownOpen = false;
            this.search = '';
        },
        subscribeToJobUpdates () {
            if (!this.stage.id || this.isSubscribed) return;
            this.isSubscribed = true;
            this.jobSubscription = this.$apollo.queries.stageJobs.subscribeToMore({
                document: stageJobsUpdatedSubscription,
                variables: {
                    stageId: this.stage.id
                },
                updateQuery: (previousData, { subscriptionData })=>{
                    const updatedJob = subscriptionData.data?.ciStageUpdated;
                    if (!updatedJob || !previousData?.ciPipelineStage) {
                        return previousData;
                    }
                    return {
                        ...previousData,
                        ciPipelineStage: {
                            ...previousData.ciPipelineStage,
                            jobs: {
                                ...previousData.ciPipelineStage.jobs,
                                nodes: previousData.ciPipelineStage.jobs.nodes.map((job)=>{
                                    if (job.name === updatedJob.name) {
                                        return merge({}, job, updatedJob);
                                    }
                                    return job;
                                })
                            }
                        }
                    };
                }
            });
        },
        unsubscribeFromJobUpdates () {
            if (this.jobSubscription) {
                this.jobSubscription?.unsubscribe();
                this.isSubscribed = false;
                this.jobSubscription = null;
            }
        },
        stageAriaLabel (title) {
            return sprintf(__('View Stage: %{title}'), {
                title
            });
        }
    }
};
